import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  acharCondicao, alternarSem, CANAIS, DEFAULT_RULES, dependeDaIA, descreverMissao, ehColetiva,
  formatPoints, MARCAS, missaoDoModelo, modelosDisponiveis, mudarTipo, platePoints, pointsLabel,
  resolveConfig, TIPOS_DE_MISSAO, tipoDaMissao, trocarCondicao, usaCondicoes,
  type ChallengeEvent, type CondicaoDeMissao, type Cuisine, type Meal, type ScoringRules,
  type TipoDeMissao,
  interacaoDe,
} from "@nhomi/core";
import { useSession } from "@/lib/auth";
import { updateConfig, watchChallenge, type Challenge } from "@/data/challenges";
import { dayKey } from "@/data/plates";
import { Empty, Spinner } from "@/ui/primitives";
import { IconBack, IconPlus, IconTrash } from "@/ui/icons";
import { descreverFalha, FalhaCard, type Falha } from "@/ui/Falha";

/* ============================================================
   Regras do desafio (só o dono)

   Tudo que era constante no código mora no documento do desafio: quanto
   vale cada refeição, quais selos existem, e eventos que dão bônus por
   juntar selos.

   O que já foi publicado NÃO muda de valor por causa de uma edição aqui:
   cada prato guarda por qual versão do motor ele foi pontuado.

   O rascunho é uma cópia solta e só o botão Salvar encosta no Firestore.
   Salvar campo a campo faria a função que refaz o placar de todo mundo
   disparar a cada tecla.
   ============================================================ */

const ABAS = [
  { id: "pontos", label: "Pontos" },
  { id: "refeicoes", label: "Refeições" },
  { id: "selos", label: "Selos" },
  { id: "eventos", label: "Missões" },
] as const;
type Aba = typeof ABAS[number]["id"];

export interface Rascunho {
  rules: ScoringRules;
  meals: Meal[];
  cuisines: Cuisine[];
  events: ChallengeEvent[];
}

/** Id estável a partir do nome, com sufixo pra nunca colidir com outro. */
export function novoId(rotulo: string, existentes: string[]): string {
  const base = String(rotulo || "item")
    .normalize("NFD").replace(/[̀-ͯ]/g, "")
    .toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")
    .slice(0, 24) || "item";
  if (!existentes.includes(base)) return base;
  for (let i = 2; i < 99; i++) if (!existentes.includes(`${base}-${i}`)) return `${base}-${i}`;
  return `${base}-${Date.now().toString(36)}`;
}

export function Rules() {
  const { cid = "" } = useParams();
  const navigate = useNavigate();
  const { user } = useSession();

  const [challenge, setChallenge] = useState<Challenge | null | undefined>(undefined);
  const [draft, setDraft] = useState<Rascunho | null>(null);
  const [aba, setAba] = useState<Aba>("pontos");
  const [salvando, setSalvando] = useState(false);
  const [falha, setFalha] = useState<Falha | null>(null);
  const [escolhendoModelo, setEscolhendoModelo] = useState(false);

  useEffect(() => watchChallenge(cid, setChallenge), [cid]);

  // O rascunho nasce uma vez. Refazê-lo a cada snapshot apagaria o que a
  // pessoa está editando.
  useEffect(() => {
    if (!challenge || draft) return;
    const c = resolveConfig(challenge);
    setDraft({
      rules: { ...DEFAULT_RULES, ...c.rules },
      meals: c.meals.map((m) => ({ ...m, weight: m.weight ?? 1 })),
      cuisines: c.cuisines.map((x) => ({ ...x })),
      events: c.events.map((e) => ({ ...e, cuisineIds: [...(e.cuisineIds ?? [])] })),
    });
  }, [challenge, draft]);

  if (challenge === undefined || !draft) return <div className="screen center"><Spinner /></div>;
  if (!challenge) {
    return <main className="screen"><Empty emoji="🤔" title="Desafio não encontrado" /></main>;
  }

  if (challenge.ownerUid !== user?.uid) {
    return (
      <main className="screen">
        <Topo cid={cid} />
        <Empty emoji="🔒" title="Só o dono do desafio configura isto">
          <p>Peça pra quem criou o desafio.</p>
        </Empty>
      </main>
    );
  }

  const modelos = modelosDisponiveis(resolveConfig({ ...challenge, ...draft }), interacaoDe(challenge));

  const mexer = (f: (d: Rascunho) => void) =>
    setDraft((d) => { if (!d) return d; const c = structuredClone(d); f(c); return c; });

  async function salvar() {
    if (!draft) return;
    setSalvando(true);
    setFalha(null);
    try {
      await updateConfig(cid, draft);
      navigate(`/c/${cid}/detalhes`);
    } catch (err) {
      setSalvando(false);
      setFalha(descreverFalha(err, "salvar as regras"));
    }
  }

  const simulado = (m: Meal, caseiro: boolean) => pointsLabel(
    platePoints({ ...resolveConfig(challenge), ...draft }, {
      homemade: caseiro, mealId: m.id, repeated: false,
    }),
  );

  return (
    <main className="screen">
      <Topo cid={cid} acao={
        <button className="topbar-action" onClick={salvar} disabled={salvando}>
          {salvando ? "Salvando…" : "Salvar"}
        </button>
      } />

      <div className="screen-body no-tabbar">
        {falha && <FalhaCard falha={falha} />}

        <div className="segmented compact">
          {ABAS.map((a) => (
            <button key={a.id} className={a.id === aba ? "active" : ""} onClick={() => setAba(a.id)}>
              {a.label}
            </button>
          ))}
        </div>

        {aba === "pontos" && (
          <>
            <div className="section-label left">Quanto vale um prato</div>
            <div className="card">
              <Num rotulo="🛒 Comprado" valor={draft.rules.bought}
                onMudar={(v) => mexer((d) => { d.rules.bought = v; })} />
              <Num rotulo="👨‍🍳 Cozinhado" valor={draft.rules.homemade}
                onMudar={(v) => mexer((d) => { d.rules.homemade = v; })} />
            </div>
            <div className="hint-row">
              O peso de cada refeição multiplica estes valores. É lá que se
              resolve o whey: uma refeição “pré/pós treino” com peso 0,25 faz
              um shake cozinhado valer {pointsLabel(draft.rules.homemade * 0.25)} em
              vez de {pointsLabel(draft.rules.homemade)}.
            </div>

            <div className="section-label left">Sequência</div>
            <div className="card">
              <Num rotulo="🔥 Bônus a partir de quantos dias seguidos" passo={1} min={1}
                valor={draft.rules.streakFrom}
                onMudar={(v) => mexer((d) => { d.rules.streakFrom = v; })} />
              <Num rotulo="Multiplicador" passo={0.1} min={1} valor={draft.rules.streakMultiplier}
                onMudar={(v) => mexer((d) => { d.rules.streakMultiplier = v; })} />
            </div>

            <div className="section-label left">Refeição repetida no mesmo dia</div>
            <div className="card">
              <Num rotulo="Quanto vale a segunda igual" valor={draft.rules.repeatMeal}
                dica="0 = pode postar e a galera chuta, mas não soma ponto de novo"
                onMudar={(v) => mexer((d) => { d.rules.repeatMeal = v; })} />
            </div>

            <div className="section-label left">Prato sem foto</div>
            <div className="card">
              <Num rotulo="📝 Quanto custa postar sem foto" valor={draft.rules.semFotoCusto}
                dica="Sai do valor do prato. A descrição do que comeu vira obrigatória."
                onMudar={(v) => mexer((d) => { d.rules.semFotoCusto = v; })} />
            </div>

            <div className="section-label left">Cravar</div>
            <div className="card">
              <Num rotulo="💸 Cravou o preço" valor={draft.rules.guessPrice}
                onMudar={(v) => mexer((d) => { d.rules.guessPrice = v; })} />
              <Num rotulo="Margem, em reais" passo={0.5} valor={draft.rules.tolPrice}
                onMudar={(v) => mexer((d) => { d.rules.tolPrice = v; })} />
              <Num rotulo="🔥 Cravou as calorias" valor={draft.rules.guessKcal}
                onMudar={(v) => mexer((d) => { d.rules.guessKcal = v; })} />
              <Num rotulo="Margem, em kcal" passo={5} valor={draft.rules.tolKcal}
                onMudar={(v) => mexer((d) => { d.rules.tolKcal = v; })} />
            </div>
            <div className="hint-row">
              Cravar é acertar, não chegar mais perto que os outros — o troféu
              de quem chegou mais perto continua existindo e não vale ponto.
              Com margem 0, acertar caloria na unidade é quase impossível; algo
              como 25 kcal faz o prêmio realmente sair de vez em quando. O
              ponto vai pra quem chutou, no dia do chute.
            </div>

            <div className="section-label left">Simulação</div>
            <div className="card breakdown">
              {draft.meals.map((m) => (
                <div key={m.id} className="bd-row">
                  <span className="bd-label">
                    {m.emoji || "🍽️"} {m.label}
                    <div className="bd-conta">peso ×{formatPoints(m.weight ?? 1)}</div>
                  </span>
                  <span className="bd-valor">{simulado(m, false)} / {simulado(m, true)}</span>
                </div>
              ))}
              <div className="bd-row total">
                <span className="bd-label">comprado / cozinhado</span>
                <span className="bd-valor">por refeição</span>
              </div>
            </div>
          </>
        )}

        {aba === "refeicoes" && (
          <>
            <div className="hint-row">
              O peso multiplica os pontos do prato. Apagar uma refeição não
              apaga os pratos que já a usaram — eles continuam lá, só somem da
              lista de opções e passam a contar com peso 1.
            </div>
            {draft.meals.map((m, i) => (
              <div key={m.id} className="card cfg-item">
                <div className="cfg-linha">
                  <input className="cfg-emoji" value={m.emoji ?? ""} maxLength={4} placeholder="🍽️"
                    aria-label="Emoji"
                    onChange={(e) => mexer((d) => { d.meals[i]!.emoji = e.target.value; })} />
                  <input className="cfg-nome" value={m.label} maxLength={24}
                    placeholder="Nome da refeição"
                    onChange={(e) => mexer((d) => { d.meals[i]!.label = e.target.value; })} />
                  <button className="cfg-x" aria-label="Apagar"
                    onClick={() => mexer((d) => { d.meals.splice(i, 1); })}>
                    <IconTrash size={18} />
                  </button>
                </div>
                <div className="cfg-linha">
                  <span className="cfg-rotulo">Peso</span>
                  <input className="cfg-peso" type="number" inputMode="decimal" step="0.25" min="0"
                    value={m.weight ?? 1}
                    onChange={(e) => mexer((d) => { d.meals[i]!.weight = Number(e.target.value) || 0; })} />
                  <span className="cfg-valor">{simulado(m, false)} / {simulado(m, true)}</span>
                </div>
              </div>
            ))}
            <button className="btn btn-white" onClick={() => mexer((d) => {
              d.meals.push({
                id: novoId("refeicao", d.meals.map((x) => x.id)),
                label: "Nova refeição", emoji: "🍽️", weight: 1,
              });
            })}>
              <IconPlus size={18} /> Adicionar refeição
            </button>
          </>
        )}

        {aba === "selos" && (
          <>
            <div className="hint-row">
              Selo não vale ponto sozinho — carimba o passaporte e alimenta os
              eventos. Apagar um selo não some dos pratos antigos.
            </div>
            <div className="card"><div className="selo-grade">
              {draft.cuisines.map((c, i) => (
                <div key={c.id} className="selo-item">
                  <span className="selo-foto">{c.emoji || "🏅"}</span>
                  <input className="selo-nome" value={c.label} maxLength={20} placeholder="Nome"
                    onChange={(e) => mexer((d) => { d.cuisines[i]!.label = e.target.value; })} />
                  <input className="selo-emoji" value={c.emoji ?? ""} maxLength={4} placeholder="emoji"
                    aria-label={`Emoji de ${c.label}`}
                    onChange={(e) => mexer((d) => { d.cuisines[i]!.emoji = e.target.value; })} />
                  <button className="cfg-x" aria-label="Apagar"
                    onClick={() => mexer((d) => {
                      const [morto] = d.cuisines.splice(i, 1);
                      // Um evento que aponta pra selo apagado nunca fecha.
                      if (morto) {
                        for (const ev of d.events) {
                          ev.cuisineIds = ev.cuisineIds.filter((x) => x !== morto.id);
                        }
                      }
                    })}>
                    <IconTrash size={16} />
                  </button>
                </div>
              ))}
            </div></div>
            <button className="btn btn-white" onClick={() => mexer((d) => {
              d.cuisines.push({
                id: novoId("selo", d.cuisines.map((x) => x.id)),
                label: "Novo selo", emoji: "🏅",
              });
            })}>
              <IconPlus size={18} /> Criar selo
            </button>
            <div className="hint-row">
              {draft.cuisines.length} {draft.cuisines.length === 1 ? "selo" : "selos"} no passaporte.
            </div>

          </>
        )}

        {aba === "eventos" && (
          <>
            <div className="hint-row">
              Uma missão é um objetivo com prazo e prêmio. O bônus entra uma
              vez só, no dia em que a pessoa fecha, e some se ela apagar os
              pratos que valeram.
            </div>
            {draft.events.length === 0 && !escolhendoModelo && (
              <Empty emoji="🎯" title="Nenhuma missão">
                <p>Crie uma pra dar um objetivo curto pro grupo.</p>
              </Empty>
            )}
            {draft.events.map((ev, i) => (
              <EditorDeMissao key={ev.id} ev={ev} draft={draft}
                mexer={(f) => mexer((d) => { f(d.events[i]!); })}
                apagar={() => mexer((d) => { d.events.splice(i, 1); })} />
            ))}

            {escolhendoModelo ? (
              <div className="card lista-crua">
                <div className="field-label chips-titulo">Começar de um modelo</div>
                {/* Agrupados pelo que a missão mede: nem toda missão é
                    sobre comer bem, e a lista longa sem grupo escondia isso. */}
                {[...new Set(modelos.map((m) => m.grupo))].map((grupo) => (
                  <div key={grupo}>
                    <div className="modelo-grupo">{grupo}</div>
                    {modelos.filter((m) => m.grupo === grupo).map((m) => (
                      <button key={m.id} className="list-row" onClick={() => {
                        mexer((d) => {
                          d.events.push(missaoDoModelo(m, novoId(m.id, d.events.map((x) => x.id)), dayKey()));
                        });
                        setEscolhendoModelo(false);
                      }}>
                        <span className="ico emoji">{m.emoji}</span>
                        <span className="label">{m.name}<span className="sub">{m.resumo} · +{formatPoints(m.bonus)} pts</span></span>
                      </button>
                    ))}
                  </div>
                ))}
                <button className="list-row" onClick={() => {
                  mexer((d) => {
                    d.events.push({
                      id: novoId("missao", d.events.map((x) => x.id)),
                      name: "Nova missão", emoji: "🎯", tipo: "contagem",
                      from: "", to: "", need: 5, bonus: 2, cuisineIds: [], condicoes: [],
                    });
                  });
                  setEscolhendoModelo(false);
                }}>
                  <span className="ico emoji">✏️</span>
                  <span className="label">Em branco<span className="sub">monta do zero</span></span>
                </button>
              </div>
            ) : (
              <button className="btn btn-white" onClick={() => setEscolhendoModelo(true)}>
                <IconPlus size={18} /> Criar missão
              </button>
            )}
          </>
        )}

        <div className="gap" />
      </div>
    </main>
  );
}

function Topo({ cid, acao }: { cid: string; acao?: React.ReactNode }) {
  return (
    <div className="topbar">
      <Link className="topbar-btn" to={`/c/${cid}/detalhes`} aria-label="Voltar"><IconBack /></Link>
      <div className="topbar-title">Regras do desafio</div>
      {acao ?? <span className="topbar-btn" />}
    </div>
  );
}

/** Campo numérico. Escreve direto no rascunho e não redesenha a lista. */
function Num(
  { rotulo, valor, onMudar, passo = 0.5, min = 0, dica }: {
    rotulo: string; valor: number; onMudar: (v: number) => void;
    passo?: number; min?: number; dica?: string;
  },
) {
  return (
    <label className="field">
      <span className="field-label">{rotulo}</span>
      <input type="number" inputMode="decimal" step={passo} min={min} value={valor}
        onChange={(e) => {
          const v = Number(e.target.value);
          onMudar(Number.isFinite(v) ? v : 0);
        }} />
      {dica && <span className="pref-hint">{dica}</span>}
    </label>
  );
}

/** Um cartão de missão: nome, tipo, janela, meta, prêmio e o que conta. */
export function EditorDeMissao(
  { ev, draft, mexer, apagar }: {
    ev: ChallengeEvent;
    draft: Rascunho;
    mexer: (f: (e: ChallengeEvent) => void) => void;
    apagar: () => void;
  },
) {
  const tipo = tipoDaMissao(ev);
  const conds = ev.condicoes ?? [];
  const unidade = TIPOS_DE_MISSAO.find((t) => t.id === tipo)?.unidade ?? "";
  const config = resolveConfig(draft);
  const hoje = dayKey();
  const janela = !ev.from && !ev.to ? "sempre valendo" : `${ev.from || "sempre"} → ${ev.to || "sem fim"}`;
  const foraDaJanela = (!!ev.from && hoje < ev.from) || (!!ev.to && hoje > ev.to);

  /** Liga/desliga uma condição de parâmetro, com o valor padrão ao ligar. */
  const alternar = (c: CondicaoDeMissao) => mexer((x) => {
    x.condicoes = trocarCondicao(x.condicoes, c.tipo,
      acharCondicao(x.condicoes, c.tipo) ? null : c);
  });
  /** Muda o número de uma condição já ligada. */
  const numero = <T extends CondicaoDeMissao["tipo"]>(tipoC: T, campo: string, v: number) =>
    mexer((x) => {
      const c = acharCondicao(x.condicoes, tipoC) as Record<string, unknown> | undefined;
      if (c) c[campo] = v;
    });

  const kcal = acharCondicao(conds, "kcalAte");
  const preco = acharCondicao(conds, "precoAte");
  const antes = acharCondicao(conds, "horaAntes");
  const depois = acharCondicao(conds, "horaDepois");
  const nota = acharCondicao(conds, "notaMin");
  const cozinha = acharCondicao(conds, "cozinha");
  const refeicao = acharCondicao(conds, "refeicao");
  const canal = acharCondicao(conds, "canal");

  const chip = (on: boolean, rotulo: string, aoTocar: () => void, key?: string) => (
    <button key={key ?? rotulo} className={`chip ${on ? "active" : ""}`} onClick={aoTocar}>{rotulo}</button>
  );
  const campoNumero = (rotulo: string, valor: number, depoisDoCampo: string, aoMudar: (v: number) => void, passo = 1) => (
    <div className="cfg-linha">
      <span className="cfg-rotulo">{rotulo}</span>
      <input className="cfg-peso" type="number" step={passo} min="0" value={valor}
        onChange={(e) => aoMudar(Number(e.target.value) || 0)} />
      <span className="cfg-rotulo">{depoisDoCampo}</span>
    </div>
  );

  return (
    <div className="card cfg-item">
      <div className="cfg-linha">
        <input className="cfg-emoji" value={ev.emoji ?? ""} maxLength={4} placeholder="🎯"
          aria-label="Emoji" onChange={(e) => mexer((x) => { x.emoji = e.target.value; })} />
        <input className="cfg-nome" value={ev.name} maxLength={40} placeholder="Nome da missão"
          onChange={(e) => mexer((x) => { x.name = e.target.value; })} />
        <button className="cfg-x" aria-label="Apagar" onClick={apagar}><IconTrash size={18} /></button>
      </div>

      <div className="cfg-linha">
        <span className="cfg-rotulo">Tipo</span>
        <select className="cfg-select" value={tipo}
          onChange={(e) => mexer((x) => {
            const novo = mudarTipo(x, e.target.value as TipoDeMissao);
            for (const k of Object.keys(x)) delete (x as unknown as Record<string, unknown>)[k];
            Object.assign(x, novo);
          })}>
          {TIPOS_DE_MISSAO.map((t) => <option key={t.id} value={t.id}>{t.nome}</option>)}
        </select>
      </div>

      <div className="cfg-linha">
        <span className="cfg-rotulo">De</span>
        <input type="date" className="cfg-data" value={ev.from ?? ""}
          onChange={(e) => mexer((x) => { x.from = e.target.value; })} />
        <span className="cfg-rotulo">até</span>
        <input type="date" className="cfg-data" value={ev.to ?? ""}
          onChange={(e) => mexer((x) => { x.to = e.target.value; })} />
      </div>
      {campoNumero("Precisa de", ev.need, unidade, (v) => mexer((x) => { x.need = Math.max(1, Math.round(v)); }))}
      {campoNumero("Prêmio", ev.bonus,
        ehColetiva(ev) ? "pts para cada um que ganhar" : "pts, uma vez, pra quem fechar",
        (v) => mexer((x) => { x.bonus = v; }), 0.5)}

      {tipo === "colecao" && (
        <div className="cfg-selos">
          {draft.cuisines.map((c) => chip(ev.cuisineIds.includes(c.id), `${c.emoji} ${c.label}`, () => mexer((x) => {
            const at = x.cuisineIds.indexOf(c.id);
            if (at >= 0) x.cuisineIds.splice(at, 1); else x.cuisineIds.push(c.id);
          }), c.id))}
        </div>
      )}

      {tipo === "atividade" && (
        <div className="cfg-selos cfg-condicoes">
          {chip(ev.acao !== "comentar", "🎯 cravar preço ou caloria", () => mexer((x) => { x.acao = "cravar"; }))}
          {chip(ev.acao === "comentar", "💬 comentar no prato dos outros", () => mexer((x) => { x.acao = "comentar"; }))}
        </div>
      )}

      {tipo === "diaCompleto" && (
        <>
          <div className="cfg-rotulo cfg-sub">O dia precisa ter…</div>
          <div className="cfg-selos cfg-condicoes">
            {draft.meals.map((m) => chip((ev.refeicoes ?? []).includes(m.id), `${m.emoji || "🍽️"} ${m.label}`, () => mexer((x) => {
              const lista = new Set(x.refeicoes ?? []);
              if (!lista.delete(m.id)) lista.add(m.id);
              x.refeicoes = draft.meals.map((y) => y.id).filter((id) => lista.has(id));
            }), m.id))}
          </div>
        </>
      )}

      {usaCondicoes(ev) && (
        <>
          <div className="cfg-rotulo cfg-sub">O prato conta quando é…</div>
          <div className="cfg-selos cfg-condicoes">
            {chip(!!acharCondicao(conds, "cozinhado"), "👨‍🍳 cozinhado", () => mexer((x) => {
              x.condicoes = trocarCondicao(trocarCondicao(x.condicoes, "comprado", null), "cozinhado",
                acharCondicao(x.condicoes, "cozinhado") ? null : { tipo: "cozinhado" });
            }))}
            {chip(!!acharCondicao(conds, "comprado"), "🛒 comprado", () => mexer((x) => {
              x.condicoes = trocarCondicao(trocarCondicao(x.condicoes, "cozinhado", null), "comprado",
                acharCondicao(x.condicoes, "comprado") ? null : { tipo: "comprado" });
            }))}
            {chip(!!kcal, `🔥 até ${kcal?.kcal ?? "X"} kcal`, () => alternar({ tipo: "kcalAte", kcal: 600 }))}
            {MARCAS.filter((m) => m.id !== "alcool").map((m) => chip(
              conds.some((c) => c.tipo === "sem" && c.marca === m.id), `sem ${m.rotulo}`,
              () => mexer((x) => { x.condicoes = alternarSem(x.condicoes, m.id); }), m.id))}
            {chip(!!preco, `💸 até R$ ${preco?.valor ?? "X"}`, () => alternar({ tipo: "precoAte", valor: 25 }))}
            {chip(!!antes, `🌅 antes das ${antes?.hora ?? "X"}h`, () => alternar({ tipo: "horaAntes", hora: 8 }))}
            {chip(!!depois, `🌙 depois das ${depois?.hora ?? "X"}h`, () => alternar({ tipo: "horaDepois", hora: 23 }))}
            {chip(!!nota, `⭐ nota ${nota?.nota ?? "X"}+`, () => alternar({ tipo: "notaMin", nota: 8 }))}
            {chip(!!acharCondicao(conds, "lugarNovo"), "📍 lugar novo pro grupo", () => alternar({ tipo: "lugarNovo" }))}
            {CANAIS.map((c) => chip(!!canal?.canais.includes(c.id), `${c.emoji} ${c.rotulo}`, () => mexer((x) => {
              const atual = new Set(acharCondicao(x.condicoes, "canal")?.canais ?? []);
              if (!atual.delete(c.id)) atual.add(c.id);
              x.condicoes = trocarCondicao(x.condicoes, "canal",
                atual.size ? { tipo: "canal", canais: CANAIS.map((y) => y.id).filter((id) => atual.has(id)) } : null);
            }), c.id))}
          </div>
          <div className="cfg-linha">
            <span className="cfg-rotulo">Refeição</span>
            <select className="cfg-select" value={refeicao?.mealId ?? ""}
              onChange={(e) => mexer((x) => {
                x.condicoes = trocarCondicao(x.condicoes, "refeicao",
                  e.target.value ? { tipo: "refeicao", mealId: e.target.value } : null);
              })}>
              <option value="">qualquer</option>
              {draft.meals.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
            </select>
          </div>
          <div className="cfg-linha">
            <span className="cfg-rotulo">Cozinha</span>
            <select className="cfg-select" value={cozinha?.cuisineId ?? ""}
              onChange={(e) => mexer((x) => {
                x.condicoes = trocarCondicao(x.condicoes, "cozinha",
                  e.target.value ? { tipo: "cozinha", cuisineId: e.target.value } : null);
              })}>
              <option value="">qualquer</option>
              {draft.cuisines.map((c) => <option key={c.id} value={c.id}>{c.emoji} {c.label}</option>)}
            </select>
          </div>
          {kcal && campoNumero("Até", kcal.kcal, "kcal por prato", (v) => numero("kcalAte", "kcal", v), 50)}
          {preco && campoNumero("Até R$", preco.valor, "por prato", (v) => numero("precoAte", "valor", v), 1)}
          {antes && campoNumero("Antes das", antes.hora, "horas (de Brasília)", (v) => numero("horaAntes", "hora", Math.min(23, v)))}
          {depois && campoNumero("Depois das", depois.hora, "horas (de Brasília)", (v) => numero("horaDepois", "hora", Math.min(23, v)))}
          {nota && campoNumero("Nota média", nota.nota, "ou mais, com 2 notas", (v) => numero("notaMin", "nota", Math.min(10, v)), 0.5)}
        </>
      )}

      <div className="hint-row cfg-resumo">
        <b>{descreverMissao(ev, config)}</b> · {janela}{foraDaJanela && " · fora da janela hoje"}
        {usaCondicoes(ev) && dependeDaIA(ev) &&
          " · só conta prato com as calorias estimadas (a correção de quem postou vale)"}
        {tipo === "colecao" && ev.cuisineIds.length > 0 && ev.cuisineIds.length < Math.max(1, ev.need) &&
          " — menos selos do que a missão exige, ninguém consegue fechar"}
        {tipo === "diaCompleto" && !(ev.refeicoes ?? []).length && " — escolha as refeições do dia"}
      </div>
    </div>
  );
}
